import { useEffect } from 'react';
import { makeRng } from '../../utils/makeRng';
import { readLegendariumState } from '../persistence/legendariumStorage';

export const LORE_FRAGMENT_CHANCE = 0.3;
// Set this to 0 for strict no-repeat behavior on newly discovered lore tiles.
// Example: 0.25 = 75% chance to get something new, 25% chance to get a repeat.
export const LORE_REPEAT_ON_NEW_TILE_CHANCE = 0;

export const LORE_SNIPPETS = [
  '“The tower wasn\'t built. It was fed. Stone came later, like scar tissue over a wound no one could close.”',
  '“Every floor you enter is shaped by someone else\'s failure. Some still remember the hands that failed them.”',
  '“There is no true center to the tower—only a depth where geometry gives up and prayer starts sounding like math.”',
  '“It\'s not a dungeon. It\'s a memory. A vast, diseased memory, shedding rooms the way fever sheds dreams.”',
  '“He spoke twelve languages, including one no human throat could survive twice.”',
  '“They say the Archmage vanished. That is a polite word. Cloth vanishes. Smoke vanishes. He came apart in ideas.”',
  '“He wasn\'t a man. He was a lock made of flesh, law, and oath. And something patient learned how to turn him.”',
  '“Every city owed him peace. The merchants called it stability, the priests called it providence, the scholars called it equilibrium. None of them knew what it cost him to hold the three words together.”',
  '“The Archmage is gone, but his shadow still wanders the tower. It does not follow light. Light follows it.”',
  '“The lower halls are older than the kingdom. The kingdom simply grew around the warning signs.”',
  '“Mercenaries came first, sent to seize the tower\'s vaults. Traders followed to price the relics. Priests followed to sanctify the theft. Scholars followed last, to name what should have remained unnamed.”',
  '“The tower hates certainty. Maps rot. Compasses spin. Only regret points in a straight line.”',
  '“Some doors open when offered blood. Others open when offered truth. The cruelest ones know the difference.”',
  '“The Archmage did not rule the three cities. He negotiated a ceasefire between hungers.”',
  '“In the city of coin, they say he hid a treasury beneath the tower. In the city of saints, they say he hid a sin. In the city of instruments, they say he hid a proof. They are all correct, and it is the same thing.”',
  '“The first explorers wrote journals. The second carved warnings. The third brought no ink, no chisel, and no names.”',
  '“There are chambers where the walls breathe slowly, as if the tower is sleeping and your footsteps are a dream it is trying to forget.”',
  '“Do not trust the statues. The tower preserves poses longer than people.”',
  '“He bound the thing below with circles, sigils, and laws of correspondence. When the bindings weakened, the circles broke first. Then the sigils. Then the laws.”',
  '“Some relics hum because they hold power. Others hum because they are afraid.”',
  '“The priests teach that the tower is a trial. The scholars insist it is a machine. The dead, when they can be persuaded to speak, call it a mouth.”',
  '“Every descent is an argument with the Archmage\'s last decision. The deeper you go, the less certain it becomes that he chose wrongly.”',
  '“There are rooms in which your reflection arrives a moment late. If it ever arrives first, do not let it see you hesitate.”',
  '“The tower keeps promises exactly as spoken, which is why so many old bargains are written in trembling hands.”',
  '“He disappeared on the night the bells rang without wind. By dawn, three delegations stood at the tower gates—one with soldiers, one with incense, one with measuring rods. None left with what they came for.”',
  '“What waits below is not evil in any way priests can use the word. It is older than morality and hungrier than prophecy.”',
  '“The Archmage\'s chambers were found empty, except for his ring, a burnt circle on the floor, and twelve fingernail marks carved into stone from the inside.”',

  '"If a corridor feels familiar, leave it. The tower learns your relief faster than your fear."\n\n-Ilya Voss',
  '"Do not trust chalk alone. I marked a stair once and found the mark waiting for me three floors higher"\n\n-Ilya Voss',
  '"When a door opens too easily, count your fingers before crossing the threshold."\n\n-Ilya Voss',
  '"Not every voice behind you is an enemy. That is what makes them dangerous."\n\n-Ilya Voss',
  '"I have seen men die for relics, truth, and glory. The tower makes no distinction between motives."\n\n-Ilya Voss',
  '"Travel light. The tower enjoys deciding, without warning, what must be carried and what must be left behind."\n\n-Ilya Voss',
  '"If your companion says they know the way, watch their hands. Confidence goes wrong there before it reaches the face."\n\n-Ilya Voss',
  '"Some mechanisms answer to ignorance. Some answer to memory. The worst answer to both."\n\n-Ilya Voss',
  '"I no longer ask whether the tower is changing. I ask whether it has noticed me yet."\n\n-Ilya Voss',
  '"There are rooms that should be looted, and rooms that should be survived. Learn the difference early."\n\n-Ilya Voss',
  '"If you find your own tracks ahead of you, turn back only once. A second retreat is how the tower measures panic."\n\n-Ilya Voss',
  '"The dead are easier to understand here than the living. The dead usually keep their shape."\n\n-Ilya Voss',
  '"I have touched locks I did not know I could open. That is not a gift. It is evidence."\n\n-Ilya Voss',
  '"Do not pray for a safe ascent. Pray to remain yourself by the time you stop climbing."\n\n-Ilya Voss',

  '"Not every silence is holy. Some are only waiting for you to speak first."\n\n-Sister Vael',
  '"The tower does not punish pride. It simply gives it room to continue."\n\n-Sister Vael',
  '"If a door opens at your touch, ask yourself who taught your hand the gesture."\n\n-Sister Vael',
  '"I have seen greed enter laughing and faith enter singing. The tower closed its jaws the same way for both."\n\n-Sister Vael',
  '"Do not follow the voice that knows your childhood name. Holiness has no need of such tricks."\n\n-Sister Vael',
  '"Some chambers test the body. The crueler ones test what you are willing to call necessary."\n\n-Sister Vael',
  '"When your companion begins to sound certain, watch them closely. Certainty is often the first thing the tower puts on."\n\n-Sister Vael',
  '"There are halls where prayer steadies the breath, and halls where prayer merely helps you hear the answering breath below."\n\n-Sister Vael',
  '"Devotion is not a shield. Bring it anyway, but do not mistake it for one."\n\n-Sister Vael',
  '"If the tower shows you mercy, leave quickly. It has mistaken you for someone else."\n\n-Sister Vael',
  '"The worst doors are not the locked ones. They are the ones that seem relieved to see you."\n\n-Sister Vael',
  '"I no longer fear what waits in the dark as much as what recognizes me there."\n\n-Sister Vael',

  '"Never trust a room that looks lived in. The tower only imitates comfort when it wants you to stand still."\n\n-Captain Rovik',
  '"If the way down feels easier than the way up, you are already paying for something."\n\n-Captain Rovik',
  '"A relic that waits in plain sight is not a reward. It is a question."\n\n-Captain Rovik',
  '"Men argue about greed until the stair narrows and someone has to choose what gets carried."\n\n-Captain Rovik',
  '"The tower does not spring traps. It lets you build them yourself with reasonable decisions."\n\n-Captain Rovik',
  '"If you must choose between the contract and the wounded, choose quickly. Hesitation is how you lose both."\n\n-Captain Rovik',
  '"I have seen honest ruins before. This place lies with architecture."\n\n-Captain Rovik',
  '"Do not weigh treasure by its price outside the tower. Inside, the cost is counted differently."\n\n-Captain Rovik',
  '"The worst bargains here are the ones no one remembers offering."\n\n-Captain Rovik',
  '"If bread is warm, back away. Hunger is easier to survive than curiosity."\n\n-Captain Rovik',
  '"A sealed door is safer than an open one. At least the sealed door is still negotiating."\n\n-Captain Rovik',
  '"The tower rarely kills you for reaching. More often, it kills you for refusing to let go."\n\n-Captain Rovik',

];

export const LORE_FRAGMENT_STORIES = [
  {
    id: 'archmage-disappearance',
    title: 'The Archmage’s Disappearance',
    parts: [
      {
        id: 'final-summons',
        label: 'I — The Final Summons',
        body:
          'On the last night the Three Cities still spoke as one, the Archmage called their emissaries to the tower. No record remains of what was said there. The ink on the official minutes faded by morning, as if ashamed to keep the words.\n\nThose who attended later disagreed on every detail except one: the Archmage seemed to pause often, not in thought, but as though listening for an answer from somewhere deep beneath the stone.\n\nBy dawn, the gathering had ended. By sunrise, he was nowhere to be found.\n\nNo sign of struggle. No body. No farewell.\n\nOnly the sense, repeated in every account, that something had already gone missing before he did.',
      },
      {
        id: 'tower-remembers',
        label: 'II — The Tower That Remembers',
        body:
          'The central tower sealed itself before first light. Locks turned without keys. Wards awoke that no living mage claimed to know. For days, strange things were reported within: steps in empty halls, doors opening to familiar hands, sigils kindling briefly at the touch of strangers who should not have known them.\n\nSome said the Archmage had hidden himself in the tower’s heart. Some said the tower had hidden him.\n\nOthers kept quieter thoughts.\n\nThat certain chambers seemed to recognize a presence not yet there.\nThat old mechanisms stirred at the approach of unknown feet.\nThat memory, in that place, did not always belong to the one who carried it.',
      },
      {
        id: 'fragments-of-a-name',
        label: 'III — Fragments of a Name',
        body:
          'In the years that followed, the story broke apart.\n\nThe priests called it sacrifice.\nThe scholars called it failure.\nThe merchants called it theft.\n\nBut among ruined stairwells and sealed observatories, another tale endured in scraps and whispers: that the Archmage did not vanish cleanly, but left behind an imprint — in stone, in spellwork, perhaps in flesh. Not a ghost. Not a prophecy. Something unfinished.\n\nThere are those who climb the tower and speak words they were never taught. Those whose hands trace ancient patterns by instinct. Those who dream of rooms before entering them, and wake with the grief of a man whose face they have never seen.\n\nThe tower does not answer them.\n\nBut sometimes, it hesitates.',
      },
    ],
  },

{
  id: 'the-tower-without-end',
  title: 'The Tower Without End',
  parts: [
    {
      id: 'a-shape-that-will-not-hold',
      label: 'I — A Shape That Will Not Hold',
      body:
        'No map of the tower survives its second reading.\n\nStairs lengthen or end too soon. Doors open where blank walls were marked the night before. Entire chambers are found above halls they once lay beneath, while familiar passages fold back into stone or return a climber to places already crossed, altered just enough to make doubt feel like error.\n\nThe scholars of the eastern kingdom called this a problem of measure. The priests of the south called it a test of pride. The western captains, less patient, simply called it cursed.\n\nYet all who entered learned the same lesson: the tower does not stay still long enough to be known.\n\nIt permits routes. It does not grant them twice.',
    },
    {
      id: 'the-many-who-entered',
      label: 'II — The Many Who Entered',
      body:
        'They come for different reasons, and die much the same.\n\nThe west sends blades and bargain-seekers, hungry for relics, leverage, or whatever force might bend the realm back into their favor. The south sends penitents, confessors, and chosen zealots, certain that somewhere above awaits either judgment or proof. The east sends surveyors, engineers, arcanists, and patient minds carrying instruments that grow less useful with every floor.\n\nSome seek the Archmage. Some seek the truth of his absence. Some seek power enough to end the old balance forever.\n\nMost leave behind only fragments: a journal without its final pages, a broken charm, a boot at the edge of a stair that no longer leads anywhere.\n\nThe tower keeps no graves.\n\nIt is content with remains.',
    },
    {
      id: 'those-who-climb-for-the-climb',
      label: 'III — Those Who Climb for the Climb',
      body:
        'Not all who enter serve a kingdom, or kneel before a cause.\n\nThere are delvers who come with no doctrine at all. No prophecy. No noble commission. They arrive light of oath and heavy with scars, drawn not by answers but by resistance itself. To them, the tower is not a holy place or a vault of statecraft. It is the purest challenge left in the world.\n\nSome want to conquer what has conquered better people. Some want to reach a floor no name has touched and live long enough to boast of it. Some, having found the outer world too small, come seeking a shape vast enough to wound their pride.\n\nThese are often the most dangerous.\n\nThey climb with fewer illusions. They ask less of the tower. And when it shifts, they do not call it injustice, only invitation.\n\nWhether they seek truth, power, absolution, or only the joy of surviving one more impossible ascent, all are changed inside it.\n\nThat may be the tower’s only constant.\n\nNot that men enter.\n\nBut that none arrive at the same height as themselves.',
    },
  ],
},

{
  id: 'field-notes-of-captain-rovik',
  title: 'Field Notes of Captain Rovik',
  parts: [
    {
      id: 'entry-one',
      label: 'Entry I — Contract Terms',
      body:
        'I took the ascent for money, which already makes me easier to trust than priests and scholars.\n\nThe commission was simple on paper: enter the tower, climb until the signs matched the sketch provided by the Court, recover a brass casket said to bear the Archmage’s seal, and return without opening it. Triple pay if the seal remained intact. Half if the casket was damaged. Nothing if lost.\n\nThere were five in my company. Two blades, one locksmith, one torchbearer, and myself. Good people, or near enough. None foolish enough to believe the tower hid treasure for the sake of being found.\n\nWe made decent progress at first. The lower halls behaved like ruins usually do: collapsed stair, bad air, old mechanisms, bones in places where bones make sense.\n\nThen we found a dining chamber set with warm plates and fresh bread.\n\nNo one touched a thing.\n\nBy then we understood the tower had stopped pretending to be dead.',
    },
    {
      id: 'entry-two',
      label: 'Entry II — The Price of Weight',
      body:
        'We found the casket on the seventh rise, nested inside a wall niche behind a line of stone figures with their faces chipped away. No seal. No lock. Only six small indentations on the lid, as if something had once been pressed there and removed.\n\nBrenn wanted to leave at once. Smart man.\n\nLysa argued that anything left in plain reach was meant to be taken, and anything meant to be taken was probably bait. Smarter woman.\n\nWe should have listened to her too.\n\nThe stair down changed after we lifted the casket. Steps narrowed. Landings leaned. The tower began shedding doors behind us, sealing rooms as if balancing an account. On the twelfth flight, Orsik slipped and drove his leg through a broken tread. Bone through the boot. Bad break.\n\nWe had to choose: carry the man or carry the prize.\n\nI said we drop the casket.\nBrenn said the Court would hang us for returning empty-handed.\nLysa said the Court was not in the stairwell with us.\n\nIn the end we tried to keep both.\n\nThat is how most bad choices survive long enough to become fatal.',
    },
    {
      id: 'entry-three',
      label: 'Entry III — Settlement',
      body:
        'Brenn is dead. Lysa is missing. Orsik asked me to leave him with a knife and one lantern, then cursed me when I actually did it.\n\nI still have the casket.\n\nI opened it two hours ago.\n\nThere was nothing inside except a folded strip of old vellum bearing a single line in merchant cipher:\n\nWhat you carry out, the tower no longer needs.\n\nI laughed when I read it. Could not help myself. Four people gone, one broken contract, and the tower sends me out with a joke.\n\nBut I have laughed less since.\n\nI cannot remember who first brought the commission to me.\nI cannot remember the face of the magistrate who signed it.\nI cannot remember whether the promised sum was silver or gold.\n\nI remember every stair.\nI remember every door.\nI remember the exact weight of the casket in my hands, though now it feels lighter than empty.\n\nThere is a gate ahead. I can see daylight through it.\n\nIf this reaches the western court, tell them the tower bargains honestly enough. It only refuses to explain the currency.\n\nAnd if a man named Rovik returns with a brass casket and asks for his pay, look closely before handing it over.\n\nHe may have left something inside that was worth more than the contract.',
    },
  ],
},

{
  id: 'journal-of-ilya-voss',
  title: 'Journal of Ilya Voss',
  parts: [
    {
      id: 'entry-one',
      label: 'Entry I — Fifth Ascent, Lower Wards',
      body:
        'I was told, before entering, that the tower confuses cowards first. I no longer believe that. It confuses precision with equal enthusiasm.\n\nI marked the west stair in red chalk at first light. By midday, the same mark was waiting for me on a sealed bronze door three levels above. Teren laughed and said one of us must have walked in circles. He stopped laughing when we found our own campfire ash in a room neither of us had seen before.\n\nWe have abandoned the idea of making a true map. I now record only what seems to matter: which sounds repeat, which doors resist, which statues have turned since the last passing.\n\nThe others still ask what we are looking for.\n\nA chamber of proof, officially. Evidence of the Archmage’s final work. A mechanism, a pattern, something the Court can name and claim.\n\nPrivately, I would settle for one corridor that remains where it is.',
    },
    {
      id: 'entry-two',
      label: 'Entry II — The Choice at the Split Stair',
      body:
        'We lost Teren today, though not all at once.\n\nThere was a stair divided into two identical ascents, each curling upward around the same hollow shaft. He took the left. I took the right. We remained in sight of one another for perhaps twenty steps. Then the walls lengthened between us without moving. His voice came from very near. His face seemed impossibly far.\n\nHe shouted that he had found a door.\nI shouted for him to wait.\nHe raised his lantern.\nI saw, very clearly, that whatever stood behind him was not my reflection.\n\nI had to choose between crossing the rail and trying to reach him, or continuing upward before the stair changed again. I hate that I hesitated. I hate more that I chose to climb.\n\nWhen I reached the landing above, there was no second stair. No Teren. Only his notebook laid open on the floor, as if set down by a careful hand.\n\nOne line had been added in writing that was not his:\n\nYou were expected higher.',
    },
    {
      id: 'entry-three',
      label: 'Entry III — If Found',
      body:
        'I no longer trust the distance between things.\n\nYesterday I opened a door and saw the observatory from my training hall in the eastern capital, broken in exactly the same places I remember from childhood. Today I passed a mirror that showed me older, robed, and standing in a room I have not yet entered. I did not look long.\n\nThere are workings here that stir before I touch them. Locks that yield too easily. Once, at a dead mechanism of brass rings and dark crystal, my hands moved before thought did. I knew where to place the pressure. I knew the sequence. I knew it with the shame of remembering something I should not.\n\nI have stopped telling myself that the tower is merely changing.\n\nIt is arranging.\n\nIf this journal is found, let it be known that I reached a chamber of high arches and an empty seat facing the inner dark. There are steps beyond it, descending where no tower should descend. I can hear someone below walking at the exact pace of my own breath.\n\nI will go down once I have gathered myself.\n\nIf another entry follows, then I was not alone.\n\nIf none does, do not send a rescue party.\n\nIt would only teach the tower my name twice.',
    },
  ],
},

{
  id: 'confessions-of-sister-vael',
  title: 'Confessions of Sister Vael',
  parts: [
    {
      id: 'entry-one',
      label: 'Entry I — First Day Within',
      body:
        'I was chosen because I do not frighten easily, and because I have never once mistaken silence for emptiness.\n\nThe priests placed ash on my brow before I entered and commanded me to seek a sign: not treasure, not mechanism, not doctrine made material, but proof that the Archmage’s absence was an act of design. If he sealed something away, we must know it. If he failed, we must know that too.\n\nThere were six of us at the gate.\n\nBy the fourth hour, we had become four.\n\nNot dead. Not yet. Simply elsewhere.\n\nBrother Ceyl turned a corner and did not return. Toman followed a voice calling him by his childhood name and vanished before any of us could seize him. I write this plainly so that memory cannot soften it later: the tower did not take them in anger. It took them gently.\n\nThat frightened me more.',
    },
    {
      id: 'entry-two',
      label: 'Entry II — The Chapel Without God',
      body:
        'We found a chapel three levels above where reason says it should have been. No dust lay on the altar. No icon had been carved there. The room was bare except for twelve kneeling shapes arranged in prayer.\n\nThey were not statues.\n\nEach wore the clothing of a different calling: a soldier, a scholar, a pilgrim, a porter, one child, one crowned figure without a face. Their hands were clasped. Their heads were bowed. None moved until we tried to pass between them.\n\nThe youngest of our company, Meret, said we should turn back.\nSerad said we had been sent to endure, not to flinch.\nI was made to choose.\n\nI ordered Meret back to the stair with the lanterns and led Serad onward through the kneeling hall.\n\nI heard Meret weeping behind us for some time.\nI heard Serad stop breathing before he noticed it himself.\n\nWhen I looked back, the kneeling shapes had become thirteen.',
    },
    {
      id: 'entry-three',
      label: 'Entry III — Final Confession',
      body:
        'I have reached a chamber of suspended bells, none of which ring when touched.\n\nBeneath them stands a narrow door, unlocked, though I have seen no key and spoken no rite. I know with certainty that something beyond it wishes to be found. I know with equal certainty that this is not the same as wishing me well.\n\nMy hands have begun to move before command. Twice now I have traced warding signs I was never taught. Once, at a sealed arch of black stone, I placed my palm upon it and felt not resistance, but recognition.\n\nI can no longer tell whether I have come here to uncover a truth, or to be reminded of one.\n\nIf these pages are recovered, tell the southern clergy this: devotion is not armor. The tower respects it no more than greed or curiosity.\n\nTell them also that some doors do not open because they have been broken.\n\nSome open because they remember the hand.\n\nI am going through now.\n\nIf I return, burn this.\nIf I do not, burn it anyway.',
    },
  ],
},

  {
  id: 'when-the-balance-broke',
  title: 'When the Balance Broke',
  parts: [
    {
      id: 'the-empty-center',
      label: 'I — The Empty Center',
      body:
        'So long as the Archmage remained in the tower, the three kingdoms could pretend their rivalries were only differences of custom. Trade, prayer, and inquiry moved in uneasy orbit around a single center, and that center did not need to speak often to be obeyed.\n\nWhen he vanished, the center did not hold.\n\nNothing fell at once. That was the cruelness of it. The roads remained open a little longer. The treaties were still recited. Envoys still crossed the borders wearing smiles like masks not yet cracked. But something invisible had withdrawn from the world, and all things once kept in proportion began, quietly, to lean.',
    },
    {
      id: 'three-answers-to-one-absence',
      label: 'II — Three Answers to One Absence',
      body:
        'Each kingdom made its own meaning from the silence.\n\nThe western lords said power had been abandoned, and that only fools leave thrones empty.\nThe southern clergy said a warning had been given, and that only the devout had ears to hear it.\nThe eastern scholars said a failure had occurred, and that any failure could be studied, entered, and corrected.\n\nSo greed called itself duty.\nFear called itself faith.\nPride called itself reason.\n\nAnd because no one could prove the others wrong, each kingdom became more certain with every passing year.',
    },
    {
      id: 'the-shape-of-ruin',
      label: 'III — The Shape of Ruin',
      body:
        'Balance did not end with battle, but with repetition.\n\nBorders hardened. Old grievances were polished and made sacred. Caravans began to travel armed. Sermons grew harsher. Experiments moved below ground. Every kingdom claimed it was preserving the realm, even as each remade itself for the struggle to come.\n\nWhat the Archmage had once kept joined, absence slowly drove apart.\n\nNow the tower stands between them not as a pillar, but as a wound. Each kingdom looks upon it and sees confirmation of its own truth. Each sends seekers upward believing they go to restore order.\n\nBut the higher they climb, the less those truths endure.\n\nBecause whatever balance was lost did not break by accident.\nAnd whatever waits at the center has had a long time to learn the shape of those who come looking for it.',
    },
  ],
},

  {
    id: 'three-kingdoms',
    title: 'The Three Kingdoms',
    parts: [
      {
        id: 'kingdom-iron-salt',
        label: 'I — The Kingdom of Iron and Salt',
        body:
          'Before the roads broke and the old banners were lowered, the western kingdom fattened itself on caravans, contracts, and war. Its ports swallowed foreign tongues. Its markets sold relics beside bread. Its captains swore loyalty only as long as coin remained honest.\n\nThey called it a kingdom of strength, though strength was never its truest craft. It knew trade better than mercy, and survival better than honor. Every alliance there was measured, every oath weighed, every friendship sharpened for use.\n\nIts people learned early that peace was only a pause between negotiations.\n\nEven now, their halls still hang with trophies taken from forgotten campaigns, and their ledgers record debts owed by men long dead.',
      },
      {
        id: 'kingdom-ash-prayer',
        label: 'II — The Kingdom of Ash and Prayer',
        body:
          'To the south, where the air grew still and incense clung to the stones, rose the kingdom of rites. Its towers were narrower, its streets quieter, its laws older than memory. There, silence was not emptiness, but discipline. Fire was not warmth, but purification. Every step toward truth required surrender of something softer.\n\nThey believed the world was not made to be understood, only endured with reverence.\n\nIts people covered fear with ceremony, and doubt with repetition. Bells marked the hours. Processions marked the seasons. Names were spoken carefully, for some names were believed to open doors that should remain closed.\n\nIn that kingdom, devotion was both refuge and blade.',
      },
      {
        id: 'kingdom-glass-thought',
        label: 'III — The Kingdom of Glass and Thought',
        body:
          'In the east stood the kingdom that trusted the hand more than the hymn and the instrument more than the dream. Its halls shone with polished metal, mirrors, lenses, engines, and unfinished diagrams. There, even wonder was dissected. Even miracles were expected to leave a residue that could be measured.\n\nThey sought mastery through understanding, and understanding through fracture.\n\nIts scholars argued that mystery was merely ignorance waiting to be dismantled. Yet for all their brilliance, their workshops were never free of shadows. Too many discoveries arrived half-formed. Too many formulas seemed remembered rather than invented.\n\nAnd so the three kingdoms endured: one of bargain, one of faith, one of reason—each certain the others mistook weakness for virtue.\n\nBut all three still turned, in their own way, toward the same tower.\nBecause however differently men name power, they kneel the same when they believe it can still be theirs.',
      },
    ],
  },
];

function clampIndex(index, length) {
  const safeLength = Math.max(0, Math.floor(Number(length) || 0));
  if (safeLength <= 0) return 0;
  const parsed = Math.floor(Number(index) || 0);
  if (parsed <= 0) return 0;
  return Math.min(safeLength - 1, parsed);
}

const LORE_RUN_STATE_KEY_PREFIX = 'dd_run_lore_state_v1';
const EMPTY_LORE_ENTRY = {
  kind: 'empty',
  id: 'empty',
  text: '“The pages have been gnawed blank by time.”',
};

function getRunLoreStateKey(runSeed) {
  return `${LORE_RUN_STATE_KEY_PREFIX}:${(Number(runSeed) >>> 0)}`;
}

function asStringArray(value) {
  return Array.isArray(value) ? value.filter((item) => typeof item === 'string' && item) : [];
}

function sanitizeRunLoreState(value) {
  const assignedByTile = value && typeof value.assignedByTile === 'object' && value.assignedByTile
    ? Object.fromEntries(
        Object.entries(value.assignedByTile).filter(
          ([key, entryId]) => typeof key === 'string' && !!key && typeof entryId === 'string' && !!entryId
        )
      )
    : {};

  return {
    discoveredEntryIds: Array.from(new Set(asStringArray(value?.discoveredEntryIds))),
    assignedByTile,
  };
}

function readRunLoreState(runSeed) {
  if (typeof window === 'undefined' || !window?.localStorage) {
    return sanitizeRunLoreState(null);
  }

  try {
    const raw = window.localStorage.getItem(getRunLoreStateKey(runSeed));
    const parsed = raw ? JSON.parse(raw) : null;
    return sanitizeRunLoreState(parsed);
  } catch {
    return sanitizeRunLoreState(null);
  }
}

function writeRunLoreState(runSeed, nextState) {
  if (typeof window === 'undefined' || !window?.localStorage) return;

  try {
    window.localStorage.setItem(
      getRunLoreStateKey(runSeed),
      JSON.stringify(sanitizeRunLoreState(nextState))
    );
  } catch {
    // ignore persistence failures
  }
}

function buildLoreTileKey({ floorLevel = 0, x = 0, y = 0, tile = '' } = {}) {
  return [
    Math.max(0, Math.floor(Number(floorLevel) || 0)),
    Math.max(0, Math.floor(Number(x) || 0)),
    Math.max(0, Math.floor(Number(y) || 0)),
    String(tile || ''),
  ].join(':');
}

function pickIndex(rng, length) {
  if (!length) return 0;
  const roll = typeof rng === 'function' ? rng() : Math.random();
  return clampIndex(Math.floor(roll * length), length);
}

function getShelfVariantSalt(tile) {
  return typeof tile === 'string' && tile.endsWith('-b') ? 0x51ed270b : 0x1f123bb5;
}

function buildLoreSeed({ tile, x = 0, y = 0, floorLevel = 0, runSeed = 0 }) {
  return (
    (Number(runSeed) >>> 0) ^
    ((Math.floor(Number(floorLevel) || 0) + 1) * 0x9e3779b9) ^
    ((Math.floor(Number(x) || 0) + 1) * 0x85ebca6b) ^
    ((Math.floor(Number(y) || 0) + 1) * 0xc2b2ae35) ^
    getShelfVariantSalt(tile) ^
    0x27d4eb2d
  ) >>> 0;
}

function formatFragmentText(story, part) {
  const storyTitle = String(story?.title || 'Lore Fragment').trim();
  const partLabel = String(part?.label || 'Fragment').trim();
  const body = String(part?.body || '').trim();

  if (!body) return `${storyTitle}
${partLabel}`;
  return `${storyTitle}
${partLabel}

${body}`;
}

function getAllLoreEntries() {
  const snippetEntries = LORE_SNIPPETS.map((text, snippetIndex) => ({
    kind: 'snippet',
    id: `snippet:${snippetIndex}`,
    snippetIndex,
    text: String(text || '').trim() || EMPTY_LORE_ENTRY.text,
  }));

  const fragmentEntries = LORE_FRAGMENT_STORIES.flatMap((story, storyIndex) => {
    const storyId = story?.id || `story-${storyIndex}`;
    const storyTitle = story?.title || 'Lore Fragment';
    const parts = Array.isArray(story?.parts) ? story.parts : [];

    return parts.map((part, partIndex) => ({
      kind: 'fragment',
      id: `fragment:${storyId}:${part?.id || `part-${partIndex}`}`,
      storyId,
      partId: part?.id || `part-${partIndex}`,
      title: storyTitle,
      label: part?.label || 'Fragment',
      text: formatFragmentText({ title: storyTitle }, part),
    }));
  });

  return [...snippetEntries, ...fragmentEntries];
}

function getLoreCatalog() {
  const entries = getAllLoreEntries();
  const byId = new Map(entries.map((entry) => [entry.id, entry]));
  return { entries, byId };
}

function pickFromPool(pool, rng) {
  if (!Array.isArray(pool) || !pool.length) return null;
  const index = pickIndex(rng, pool.length);
  return pool[index] || pool[0] || null;
}

function pickLoreEntryFromAvailable(availableEntries, rng) {
  if (!Array.isArray(availableEntries) || !availableEntries.length) {
    return EMPTY_LORE_ENTRY;
  }

  const fragments = availableEntries.filter((entry) => entry?.kind === 'fragment');
  const snippets = availableEntries.filter((entry) => entry?.kind === 'snippet');
  const fragmentRoll = typeof rng === 'function' ? rng() : Math.random();

  if (fragments.length && snippets.length) {
    return fragmentRoll < LORE_FRAGMENT_CHANCE
      ? pickFromPool(fragments, rng)
      : pickFromPool(snippets, rng);
  }

  if (fragments.length) return pickFromPool(fragments, rng);
  if (snippets.length) return pickFromPool(snippets, rng);
  return EMPTY_LORE_ENTRY;
}

function shouldPreferRepeatOnNewTile(rng) {
  if (LORE_REPEAT_ON_NEW_TILE_CHANCE <= 0) return false;
  const roll = typeof rng === 'function' ? rng() : Math.random();
  return roll < LORE_REPEAT_ON_NEW_TILE_CHANCE;
}

export function getLoreEntryForTile({ tile, x = 0, y = 0, floorLevel = 0, runSeed = 0 } = {}) {
  if (typeof tile !== 'string' || !tile.startsWith('bookshelf-')) return null;

  const tileKey = buildLoreTileKey({ tile, x, y, floorLevel });
  const { entries, byId } = getLoreCatalog();
  const state = readRunLoreState(runSeed);
  const assignedEntryId = state.assignedByTile[tileKey];

  if (assignedEntryId) {
    const assignedEntry = byId.get(assignedEntryId);
    if (assignedEntry?.text) {
      if (!state.discoveredEntryIds.includes(assignedEntryId)) {
        writeRunLoreState(runSeed, {
          ...state,
          discoveredEntryIds: [...state.discoveredEntryIds, assignedEntryId],
        });
      }
      return assignedEntry;
    }

    const repairedAssignments = { ...state.assignedByTile };
    delete repairedAssignments[tileKey];
    writeRunLoreState(runSeed, {
      ...state,
      assignedByTile: repairedAssignments,
    });
  }

  const discoveredIds = new Set(state.discoveredEntryIds);
  const undiscoveredEntries = entries.filter((entry) => entry?.id && !discoveredIds.has(entry.id));
  const discoveredEntries = entries.filter((entry) => entry?.id && discoveredIds.has(entry.id));
  const rng = makeRng(buildLoreSeed({ tile, x, y, floorLevel, runSeed }));

  const preferRepeat =
    undiscoveredEntries.length > 0 &&
    discoveredEntries.length > 0 &&
    shouldPreferRepeatOnNewTile(rng);

  const candidatePool = preferRepeat ? discoveredEntries : undiscoveredEntries;
  const fallbackPool = preferRepeat ? undiscoveredEntries : discoveredEntries;
  const chosenEntry =
    pickLoreEntryFromAvailable(candidatePool, rng) ||
    pickLoreEntryFromAvailable(fallbackPool, rng) ||
    EMPTY_LORE_ENTRY;

  if (!chosenEntry?.id || chosenEntry.kind === 'empty') {
    return chosenEntry;
  }

  const nextDiscoveredEntryIds = discoveredIds.has(chosenEntry.id)
    ? [...discoveredIds]
    : [...discoveredIds, chosenEntry.id];

  writeRunLoreState(runSeed, {
    discoveredEntryIds: nextDiscoveredEntryIds,
    assignedByTile: {
      ...state.assignedByTile,
      [tileKey]: chosenEntry.id,
    },
  });

  return chosenEntry;
}

export function useLegendariumLoreSync({
  activeLore,
  barredDoorLore,
  unlockLegendariumEntries,
  onLoreRecorded,
}) {
  useEffect(() => {
    if (!activeLore || typeof unlockLegendariumEntries !== 'function') return;

    const alreadyRecorded = readLegendariumState().lore.includes(activeLore);
    unlockLegendariumEntries({ lore: [activeLore] });

    if (!alreadyRecorded && typeof onLoreRecorded === 'function') {
      onLoreRecorded(activeLore);
    }
  }, [activeLore, unlockLegendariumEntries, onLoreRecorded]);

  useEffect(() => {
    if (!barredDoorLore || typeof unlockLegendariumEntries !== 'function') return;

    const alreadyRecorded = readLegendariumState().lore.includes(barredDoorLore);
    unlockLegendariumEntries({ lore: [barredDoorLore] });

    if (!alreadyRecorded && typeof onLoreRecorded === 'function') {
      onLoreRecorded(barredDoorLore);
    }
  }, [barredDoorLore, unlockLegendariumEntries, onLoreRecorded]);
}
